import ColumnBase from "./ColumnBase.jsx";
import { WIDGET_KEYS } from "../../other/constants/WidgetKeys.jsx";
import { SWIVU_STANDARD_ICONS } from "../../other/constants/IconKeys.jsx";
import { ICON_COL_DEF_WIDTH, VIEW_TYPES } from "../../other/constants/OtherKeys.jsx";
import { ColumnStore, EventHelper, Tooltip } from "@bryntum/schedulerpro";
import { copyTextToClipboard, openGeneralWidgetFromGridRecord } from "../../other/utils/utils.jsx";
import { WIDGET_REGISTRY } from "../../other/constants/EntityManagerConfig.tsx";
import { EDITOR_KEYS } from "../../other/constants/EditorKeys.jsx";

export default class ContactColumn extends ColumnBase {
  static get $name() {
    return "ContactColumn";
  }

  static get type() {
    return WIDGET_KEYS.contactColumn;
  }

  static get defaults() {
    return {
      text: "Contact",
      width: 30,
      minWidth: ICON_COL_DEF_WIDTH,
      editor: false,
      searchable: "center",
      align: "center",
      cellCls: "cursor-pointer",
      cls: "swivu-icon-column",
      sealed: true,
      filterable: false,
      htmlEncode: false,
      field: "hasContacts",
      headerRenderer() {
        return `<i class="${SWIVU_STANDARD_ICONS[VIEW_TYPES.contacts]}"/>`
      },
      renderer({ value }) {
        return `<i class="${SWIVU_STANDARD_ICONS[VIEW_TYPES.contacts]} swivu-icon-tooltip ${value ? 'text-[#0a88e9]' : ''}"/>`
      },
      groupRenderer({ groupRowFor }) {
        return `<i class="${SWIVU_STANDARD_ICONS[VIEW_TYPES.contacts]} ${groupRowFor ? 'text-[#0a88e9]' : ''}"/>`
      },
    }
  }

  onLoad(project, otherParams) {
    this.project = project;
    this.otherParams = otherParams;
    this.editorHandler = otherParams?.editorHandler;
    if (!this.swivuContactTooltip) {
      this.swivuContactTooltip = new Tooltip({
        forElement: this.grid.element,
        forSelector: ".swivu-icon-tooltip",
        allowOver: true,
        cls: "swivu-contact-tooltip",
        scrollable: true,
        getHtml: this.getContactTooltipContent.bind(this),
      })
      this.on({
        destroy: this.onColumnDestroy,
        thisObj: this,
        once: true,
      })
    }
  }

  getContactTooltipContent({ activeTarget, tip }) {
    const record = this.grid.getRecordFromElement(activeTarget);
    if (record.contacts.length) {
      if (this.onTooltipClick) {
        this.onTooltipClick();
        this.onTooltipClick = null;
      }
      this.onTooltipClick = EventHelper.on({
        element: tip.element,
        click: (e) => this.onContactClick(e, record),
        thisObj: this
      })
      return  {
        children: [{
          tag: "div",
          class: "swivu-contact-tooltip-content flex flex-col gap-3 p-1",
          children: record.contacts.map(({ contactRecord }, index) => {
            const contactPills = [
              {
                tag: "div",
                class: "contact-pill contact-header font-medium text-sm cursor-pointer hover:bg-accent/80 transition-colors rounded-md px-3 py-2",
                text: contactRecord.name
              }
            ];

            if (contactRecord.email) {
              contactPills.push({
                tag: "div",
                class: "contact-pill contact-email text-xs text-muted-foreground cursor-pointer hover:bg-accent/80 transition-colors rounded-md px-3 py-1.5 flex items-center gap-2",
                html: `<i class="fa fa-envelope w-4"></i><span>${contactRecord.email}</span>`
              });
            }
            if (contactRecord.phone) {
              contactPills.push({
                tag: "div",
                class: "contact-pill contact-phone text-xs text-muted-foreground cursor-pointer hover:bg-accent/80 transition-colors rounded-md px-3 py-1.5 flex items-center gap-2",
                html: `<i class="fa fa-phone w-4"></i><span>${contactRecord.phone}</span>`
              });
            }

            return {
              tag: "div",
              dataset: {
                contactId: contactRecord.id
              },
              class: `tooltip-contact-container flex flex-col gap-1 ${index > 0 ? 'pt-3 border-t border-border' : ''}`,
              children: contactPills
            }
          })
        }]
      }
    } else {
      tip.hide();
    }
  }

  onContactClick(event, record) {
    if (this.onContactTimeout) clearTimeout(this.onContactTimeout);

    this.onContactTimeout = setTimeout(() => {
      // Find the contact pill element (might be nested due to icons/spans)
      const pillElement = event.target.closest('.contact-pill');
      if (!pillElement) return;

      // Find the contact container
      const tooltipContactContainerEl = pillElement.closest('.tooltip-contact-container');
      if (!tooltipContactContainerEl) return;

      const contactRecord = this.project.contactsStore.getById(tooltipContactContainerEl.dataset.contactId);
      if (!contactRecord) return;

      let pillType = "phone";

      if (pillElement.classList.contains("contact-header")) {
        pillType = "header";
      } else if (pillElement.classList.contains("contact-email")) {
        pillType = "email";
      }

      switch (pillType) {
        case "header":
          // for dblClick on header
          if (event.detail === 2) {
            // Open existing contact (not phantom)
            const clientRecord = record.isClient ? record : null;
            this.otherParams.editorHandler.openEditor(
              EDITOR_KEYS.contactEditor,
              { id: contactRecord.id },
              {
                contact: contactRecord,
                entityRecord: clientRecord
              },
              {
                by: "recordId",
                useExisting: true
              }
            );
          } else {
            const copyText = [contactRecord.name, contactRecord.email, contactRecord.phone].filter(Boolean).join('\n');
            copyTextToClipboard(copyText, `${contactRecord.name} has been copied to clipboard`);
          }
          break;
        case "email":
          if (contactRecord.email) {
            copyTextToClipboard(contactRecord.email, `${contactRecord.name}'s email has been copied to clipboard`);
          }
          break;
        default:
          if (contactRecord.phone) {
            copyTextToClipboard(contactRecord.phone, `${contactRecord.name}'s phone number has been copied to clipboard`);
          }
          break;
      }
    }, 800);
  }


  onColumnDestroy() {
    this.swivuContactTooltip.destroy();
    if (this.onTooltipClick) {
      this.onTooltipClick();
      this.onTooltipClick = null;
    }
  }

  onSwivuColumnClick(e) {
    openGeneralWidgetFromGridRecord(this.editorHandler, e.record, WIDGET_REGISTRY.contacts.id);
  }
}

ColumnStore.registerColumnType(ContactColumn);
